import { useModeAwareQuery } from "./useModeAwareQuery";
import { useApi } from "./useApi";

export interface WorkflowListItemApi {
  id: number;
  disputeTypeId: number;
  disputeTypeName: string;
  name: string;
  description: string | null;
  status: string;
  createdByUserId: number | null;
  createdByUserName: string | null;
  createdAt: string;
  updatedAt: string;
  stageCount: number;
  transitionCount: number;
}

export interface WorkflowStageApi {
  id: number;
  workflowId: number;
  name: string;
  description: string | null;
  sortOrder: number;
  color: string | null;
  icon: string | null;
  properties: string | null;
  notifications: string | null;
  requiredFields: string | null;
  allowedActions: string | null;
  createdAt: string;
  updatedAt: string;
}

const mockWorkflows: WorkflowListItemApi[] = [
  {
    id: 1,
    disputeTypeId: 1,
    disputeTypeName: "Billing Error",
    name: "Billing Error Resolution Workflow",
    description: "Standard workflow for billing error disputes",
    status: "active",
    createdByUserId: 1,
    createdByUserName: "Admin User",
    createdAt: "2024-01-01T00:00:00Z",
    updatedAt: "2024-01-01T00:00:00Z",
    stageCount: 3,
    transitionCount: 2,
  },
];

const mockStages: WorkflowStageApi[] = [
  {
    id: 1,
    workflowId: 1,
    name: "Initial Review & Categorization",
    description: "Dispute received and automatically categorized",
    sortOrder: 1,
    color: null,
    icon: null,
    properties: null,
    notifications: null,
    requiredFields: null,
    allowedActions: null,
    createdAt: "2024-01-01T00:00:00Z",
    updatedAt: "2024-01-01T00:00:00Z",
  },
  {
    id: 2,
    workflowId: 1,
    name: "Investigation & Evidence Gathering",
    description: "Collecting relevant documents and customer communications",
    sortOrder: 2,
    color: null,
    icon: null,
    properties: null,
    notifications: null,
    requiredFields: null,
    allowedActions: null,
    createdAt: "2024-01-01T00:00:00Z",
    updatedAt: "2024-01-01T00:00:00Z",
  },
  {
    id: 3,
    workflowId: 1,
    name: "Final Resolution Notice",
    description: "Resolution approved and customer notified",
    sortOrder: 3,
    color: null,
    icon: null,
    properties: null,
    notifications: null,
    requiredFields: null,
    allowedActions: null,
    createdAt: "2024-01-01T00:00:00Z",
    updatedAt: "2024-01-01T00:00:00Z",
  },
];

export function useWorkflowsList() {
  const { apiGet } = useApi();

  return useModeAwareQuery<WorkflowListItemApi[], Error>(
    ["workflows"],
    async ({ signal }) => {
      return apiGet<WorkflowListItemApi[]>("/api/v1/settings/workflows", undefined, signal);
    },
    mockWorkflows,
    { staleTime: 60 * 1000, refetchOnWindowFocus: false }
  );
}

export function useWorkflowStages(workflowId: number | null) {
  const { apiGet } = useApi();

  return useModeAwareQuery<WorkflowStageApi[], Error>(
    ["workflow-stages", workflowId],
    async ({ signal }) => {
      if (!workflowId) return [];
      return apiGet<WorkflowStageApi[]>(
        `/api/v1/settings/workflows/${workflowId}/stages`,
        undefined,
        signal
      );
    },
    mockStages,
    {
      enabled: !!workflowId,
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );
}
